import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { Item } from "@/lib/erp";
import type { ItemWithVariants } from "@/lib/erp/types/item";
import type { ItemListParams } from "@/lib/erp/items";
import { ITEM_LIST_FIELDS } from "@/lib/erp/items";
import type { FilterDescriptor } from "@/lib/erp";
import type { FrappeFilterArray } from "@/lib/erp/filterMapper";
import { useDocTypeListWithPagination } from "@/features/common/api/useDocTypeListWithPagination";
import { stableStringify } from "@/lib/utils/stableStringify";
import { validateFilters, mapFiltersToFrappe } from "@/lib/erp/filterMapper";
import { getQueryOptions } from "@/lib/config/queryConfig";
import type { FrappeListResponse } from "@/lib/frappe-client";

/**
 * Search fields for Item doctype.
 * Must include "name" because frappe.desk.search.search_link returns document names.
 * Keep in sync with VARIANT_SEARCH_FIELDS in item_list.py
 */
const ITEM_SEARCH_FIELDS = ["name", "item_code", "item_name"] as const;

/**
 * Legacy hook - fetches all items (client-side filtering)
 * @deprecated Use useInventoryListWithPagination for server-side filtering
 */
export function useInventoryList(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["items", "list", filters],
    queryFn: () => erp.items.list(filters),
  });
}

/**
 * Parameters for useInventoryListWithPagination
 * 
 * Note: filterDescriptors is the preferred way to pass filters.
 * The preMappedFilters param is for legacy/advanced use only (pre-mapped Frappe filters).
 */
export type InventoryListParams = Omit<ItemListParams, "filters"> & {
  /**
   * FilterDescriptor[] to be converted to Frappe format internally.
   * This is the preferred way to pass filters for normal inventory use.
   */
  filterDescriptors?: FilterDescriptor[];
  /**
   * Legacy/advanced: pass pre-mapped Frappe filter array.
   * Prefer filterDescriptors in new code.
   * 
   * @deprecated Use filterDescriptors instead. This is kept for backwards compatibility.
   */
  preMappedFilters?: FrappeFilterArray;
  /**
   * @deprecated Use preMappedFilters instead for clarity. Kept for backwards compatibility.
   */
  filters?: FrappeFilterArray;
  /**
   * Optional: fields to fetch from backend.
   * If not provided, defaults to ITEM_LIST_FIELDS.
   * If provided, overrides the default fields list.
   */
  fields?: string[];
};

/**
 * Hook for fetching items with server-side pagination and filtering
 * 
 * This is a thin wrapper around the generic useDocTypeListWithPagination hook,
 * providing Item-specific defaults (doctype: "Item", searchFields: ["name", "item_code", "item_name"]).
 * 
 * IMPORTANT: This hook uses the generic docTypeListWithPagination path, which means:
 * - It does NOT automatically inject `disabled = 0` filter (unlike erp.items.listWithPagination)
 * - It does NOT handle `_assigned_to` filter routing to custom method (unlike erp.items.listWithPagination)
 * 
 * If you need `_assigned_to` filtering or automatic `disabled = 0` injection, consider:
 * - Using erp.items.listWithPagination directly (requires mapping filterDescriptors to Frappe format first)
 * - Or adding those filters explicitly via filterDescriptors
 * 
 * @see erp.items.listWithPagination for Item-specific logic including _assigned_to handling
 */
export function useInventoryListWithPagination(params: InventoryListParams) {
  // Support both preMappedFilters (preferred) and filters (deprecated) for backwards compatibility
  const { preMappedFilters, filters, ...restParams } = params;
  const legacyFilters = preMappedFilters || filters;

  return useDocTypeListWithPagination<Item>({
    doctype: "Item",
    searchFields: [...ITEM_SEARCH_FIELDS],
    fields: params.fields ?? [...ITEM_LIST_FIELDS], // Use provided fields or default
    listMethodOverride: "zaviago_backend.api.doc_list.list_with_system_fields", // Phase 2: Use method with comment counts
    ...restParams,
    // Map to the generic hook's parameter name
    filters: legacyFilters,
  });
}

export function useInventoryItem(name: string) {
  return useQuery({
    queryKey: ["items", "detail", name],
    queryFn: () => erp.items.detail(name),
    enabled: !!name,
  });
}

export function useItemPrices(itemCode: string) {
  return useQuery({
    queryKey: ["items", "prices", itemCode],
    queryFn: () => erp.items.prices(itemCode),
    enabled: !!itemCode,
  });
}

export function useItemStock(itemCode: string) {
  return useQuery({
    queryKey: ["items", "stock", itemCode],
    queryFn: () => erp.items.stock(itemCode),
    enabled: !!itemCode,
  });
}

/**
 * Hook for fetching items with variants nested under their parent items
 * 
 * This hook uses a dedicated backend method that returns items with variants
 * attached to their parent rows. Variants are expanded by default in the table.
 * 
 * Uses distinct query key: ["doctype-list", "Item", "with-variants", params]
 * to avoid cache conflicts with the flat list view.
 */
export function useItemListWithVariants(params: InventoryListParams) {
  const {
    filterDescriptors,
    preMappedFilters,
    filters: legacyFilters,
    searchQuery,
    pageIndex = 0,
    pageSize = 20,
    orderBy,
    fields,
  } = params;

  // Build search or_filters from searchFields
  // Include "name" because search_link API returns document names which users may select
  const searchOrFilters: Array<[string, "like", string]> | undefined =
    searchQuery?.trim()
      ? ITEM_SEARCH_FIELDS.map((field) => [field, "like", `%${searchQuery.trim()}%`] as [string, "like", string])
      : undefined;

  // Map FilterDescriptor[] -> FrappeFilterArray
  let frappeFiltersForQuery: FrappeFilterArray | undefined;

  const preMapped = preMappedFilters || legacyFilters;
  if (preMapped) {
    frappeFiltersForQuery = preMapped;
  } else if (filterDescriptors && filterDescriptors.length > 0) {
    // Validate filters - invalid filters are still mapped but may produce unexpected results
    validateFilters("Item", filterDescriptors);
    frappeFiltersForQuery = mapFiltersToFrappe("Item", filterDescriptors);
  }

  // Stable query key
  const queryKey = [
    "doctype-list",
    "Item",
    "with-variants",
    {
      filters: stableStringify(frappeFiltersForQuery || []),
      orFilters: stableStringify(searchOrFilters || []),
      searchQuery: searchQuery || undefined,
      pageIndex,
      pageSize,
      orderBy: orderBy || undefined,
      fields: fields || undefined,
    },
  ];

  // Query function
  const queryFn = async () => {
    return erp.items.listWithVariants({
      filters: frappeFiltersForQuery,
      or_filters: searchOrFilters,
      pageIndex,
      pageSize,
      orderBy,
      fields: fields ?? [...ITEM_LIST_FIELDS],
    });
  };

  return useQuery<FrappeListResponse<ItemWithVariants>>({
    queryKey,
    queryFn,
    ...getQueryOptions("list"),
    placeholderData: (previousData) => previousData,
  });
}
